import makeDebugger from 'javascripts/util/devLog';
import ReactiveRegister, {
  type Change,
  type ReactiveAction,
} from 'javascripts/reactive_register';
import { type ApplicationController } from '@aha-app/mvc';

interface SubscribeOptions {
  ignoreOwnChanges?: boolean;
}

type ReactiveChange = Change;

type SubscribeCallback = (
  parentId: string,
  childId: string,
  change: ReactiveChange
) => void;

type SubscribeBatchCallback = (changes: ReactiveChange[]) => void;

const debug = makeDebugger('framework:controller:subscriptions');

export interface IHasReactiveRailsBehaviors {
  /**
   * Subscribe to a reactive update based on pattern and action.
   *
   * @param pattern a reactive update pattern. Example: "Project-123/Feature-456"
   * @param actionOrCallback a function that receives the parentId and childId of a reactive update. Example: callback("projects-123", "features-456")
   * @param actions one of ["create", "update", "destroy"]. Defaults to all.
   */
  subscribe(
    pattern: string | string[],
    actionOrCallback: ReactiveAction | ReactiveAction[] | SubscribeCallback,
    callback?: SubscribeCallback,
    options?: SubscribeOptions
  ): string;

  /**
   * Subscribe to a reactive update based on pattern and action.
   *
   * @param pattern a reactive update pattern. Example: "Project-123/Feature-456"
   * @param callback a function that receives the parentId and childId of a reactive update. Example: callback("projects-123", "features-456")
   * @param actions one of ["create", "update", "destroy"]. Defaults to all.
   */
  subscribeBatch(
    pattern: string | string[],
    callback: SubscribeBatchCallback,
    options?: { actions?: ReactiveAction | ReactiveAction[] }
  ): string;

  unsubscribe(subscriptionId: string): void;

  unsubscribeAll(): void;

  /**
   * @param callback optional callback to execute with reactive updates paused
   */
  pauseReactiveUpdates<T>(callback: () => Promise<T>): Promise<T>;

  resumeReactiveUpdates(): void;
}

/**
 * Mixin for ApplicationController so that it can subscribe to reactive updates.
 *
 * Use like this in TS files
 *
 * interface MyController extends IHasReactiveRailsBehaviors {}
 * @HasReactiveRailsBehaviors
 * class MyController extends ApplicationController {
 *
 * or like this in JS files
 *
 * class MyController extends HasReactiveRailsBehaviors(ApplicationController) {
 *
 */
export function HasReactiveRailsBehaviors<
  T extends { new (...args: any[]): ApplicationController }
>(originalClass: T) {
  return class extends originalClass implements IHasReactiveRailsBehaviors {
    subscriptions: string[] | undefined;
    _pauseReactiveUpdates: boolean = false;

    destroy() {
      super.destroy();

      // Unregister reactive updates.
      this.unsubscribeAll();
    }

    /**
     * Subscribe to a reactive update based on pattern and action.
     *
     * @param pattern a reactive update pattern. Example: "Project-123/Feature-456"
     * @param action_or_callback one of ["create", "update", "destroy"]
     * @param callback a function that receives the parentId and childId of a reactive update. Example: callback("projects-123", "features-456")
     */
    subscribe(
      pattern: string | string[],
      actionOrCallback: string | string[] | SubscribeCallback,
      callback?: SubscribeCallback,
      options: SubscribeOptions = {}
    ) {
      const { ignoreOwnChanges = true } = options;

      if (!this.subscriptions) {
        this.subscriptions = [];
      }

      let subscribedActions: string[];

      if (typeof actionOrCallback === 'function') {
        callback = actionOrCallback;
        subscribedActions = ['create', 'update', 'destroy'];
      } else if (actionOrCallback instanceof Array) {
        subscribedActions = actionOrCallback;
      } else if (typeof actionOrCallback === 'string') {
        subscribedActions = [actionOrCallback];
      }

      // Use reactive updates to detect record changes.
      const subscriptionId = `controller-${this.id}-${subscribedActions.join(
        ','
      )}-${pattern}`;

      if (this.subscriptions.includes(subscriptionId)) {
        console.warn(
          `Re-registering an already registered reactive pattern for this controller: ${subscriptionId}`
        );
        this.unsubscribe(subscriptionId);
      }

      this.subscriptions.push(subscriptionId);
      ReactiveRegister.register(
        subscriptionId,
        pattern,
        async (
          change,
          ownPageChanges,
          ownComponentChanges,
          messageClientId
        ) => {
          if (this._pauseReactiveUpdates) {
            debug('Reactive updates paused');
            return;
          }

          if (
            ignoreOwnChanges &&
            messageClientId === window.frameworkClientId()
          ) {
            debug(`Ignoring our own change ${change.path}`);
            return;
          }

          if (subscribedActions.includes(change.action)) {
            debug(`Handling update ${change.path}`);
            // Split parent and child key into components.
            const [parentId, childId] = change.path.split('/');

            callback(parentId, childId, change);
          }
        }
      );
      return subscriptionId;
    }

    /**
     * Subscribe to a reactive update with all matching changes in a single callback
     *
     * @param pattern a reactive update pattern. Example: "Project-123/Feature-456"
     * @param callback a function that receives the parentId and childId of a reactive update. Example: callback("Projects-123", "Features-456")
     */
    subscribeBatch(
      pattern: string | string[],
      callback: SubscribeBatchCallback,
      options: { actions?: ReactiveAction | ReactiveAction[] } = {}
    ) {
      let actions = options?.actions;
      actions = actions || ['create', 'update', 'destroy'];
      actions = actions instanceof Array ? actions : [actions];

      if (!this.subscriptions) this.subscriptions = [];

      // Use reactive updates to detect record changes.
      const subscriptionId = `controller-${this.id}-${actions.join(
        ','
      )}-${pattern}`;

      if (this.subscriptions.includes(subscriptionId)) {
        console.warn(
          `Re-registering an already registered reactive pattern for this controller: ${subscriptionId}`
        );
        this.unsubscribe(subscriptionId);
      }

      this.subscriptions.push(subscriptionId);

      ReactiveRegister.registerBatchedChanges(
        subscriptionId,
        pattern,
        async (
          changes,
          ownPageChanges,
          ownComponentChanges,
          messageClientId
        ) => {
          if (this._pauseReactiveUpdates) {
            debug('Reactive updates paused');
            return;
          }

          if (messageClientId === window.frameworkClientId()) {
            debug(`Ignoring our own change`);
            return;
          }

          callback(changes);
        }
      );
      return subscriptionId;
    }

    unsubscribe(subscriptionId: string) {
      ReactiveRegister.deregister(subscriptionId);

      if (!this.subscriptions) return;

      const index = this.subscriptions.indexOf(subscriptionId);
      if (index >= 0) this.subscriptions.splice(index, 1);
    }

    unsubscribeAll() {
      if (!this.subscriptions) return;

      this.subscriptions.slice().forEach(subscriptionId => {
        this.unsubscribe(subscriptionId);
      });
    }

    pauseReactiveUpdates<T>(callback?: () => Promise<T>) {
      this._pauseReactiveUpdates = true;

      if (callback) {
        return callback().finally(() => {
          this.resumeReactiveUpdates();
        });
      }
    }

    resumeReactiveUpdates() {
      this._pauseReactiveUpdates = false;
    }
  };
}
